"use client";

import type React from "react";
import { useEffect, useState } from "react";
import type { Notification } from "../types/notifications";
import { Link } from "react-router-dom";
import dreamWorldProvider from "../DreamWorldProvider";
import { useSelector } from "react-redux";
import { selectUserGlobalData } from "../store/dreamworldSlice";
import { motion } from "framer-motion";
import PageLayout from "../components/ui/PageLayout";

const NotificationsPage: React.FC = () => {
  const user = useSelector(selectUserGlobalData);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const notifs = await dreamWorldProvider.getNotifications(
          user?.userData?.uid
        );
        setNotifications(notifs);
        await dreamWorldProvider.markNotificationsAsRead(user?.userData?.uid);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user]);

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Notifications
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                Stay updated with your latest interactions and activities
              </p>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
            <p className="mt-4 text-gray-400">Loading notifications...</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            {notifications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-800 rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 text-gray-600">🔔</div>
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  No notifications yet
                </h3>
                <p className="text-gray-400">
                  You'll see notifications here when others interact with your
                  content
                </p>
              </motion.div>
            ) : (
              notifications.map((notif, index) => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  className={`relative overflow-hidden rounded-xl border transition-all duration-300 ${
                    notif.read
                      ? "bg-gray-800/50 border-gray-700/50 hover:bg-gray-800/70"
                      : "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/30 hover:from-blue-900/40 hover:to-purple-900/40"
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-purple-500/5 backdrop-blur-sm"></div>

                  <Link
                    to={`/content/${notif.contentId}`}
                    className="relative block p-6"
                  >
                    <div className="flex items-center space-x-4">
                      {notif.sender?.avatarUrl && (
                        <div className="relative">
                          <img
                            src={notif.sender.avatarUrl || "/placeholder.svg"}
                            alt={notif.sender.username}
                            className="w-12 h-12 rounded-full border-2 border-purple-400/30"
                          />
                          {!notif.read && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-slate-900"></div>
                          )}
                        </div>
                      )}

                      <div className="flex-1">
                        <p className="text-white">
                          <span className="font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                            {notif.sender?.username || notif.senderId}
                          </span>{" "}
                          <span className="text-gray-300">
                            {notif.type === "like"
                              ? "liked your post."
                              : "commented on your post."}
                          </span>
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          {new Date(
                            (notif.timestamp as any)?.seconds * 1000
                          ).toLocaleString()}
                        </p>
                      </div>

                      {notif.content?.thumbnailUrl && (
                        <div className="relative">
                          <img
                            src={
                              notif.content.thumbnailUrl || "/placeholder.svg"
                            }
                            alt="content thumbnail"
                            className="w-12 h-12 rounded-lg border border-gray-600/50 object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </PageLayout>
    </div>
  );
};

export default NotificationsPage;
